import { useMemo, useState } from "react";
import { Loader2, CircleCheck, TriangleAlert, School, Wallet, Hourglass } from "lucide-react";
import SedecShell from "@/components/layout/SedecShell";
import { Card } from "@/components/ui/card";
import { useSuiviEcoles } from "@/lib/backoffice-hooks";
import { friendlyError } from "@/lib/errors";
import { formatDateShort, formatDateTime, formatXof } from "@/lib/format";
import type { SuiviEcole } from "@/lib/api";

/**
 * Suivi des écoles par le SEDEC (audit SEDEC-07 du 21/10/2026) : une ligne par
 * école active de l'année active, avec ses feux rouges calculés par le serveur.
 * Lecture seule ; aucune donnée personnelle (codes, noms d'école, volumes).
 */

type Tri = "alertes" | "nom" | "inscrits";

const TRIS: Record<Tri, { label: string; compare: (a: SuiviEcole, b: SuiviEcole) => number }> = {
  alertes: {
    label: "Nombre d'alertes",
    compare: (a, b) => b.feuxRouges.length - a.feuxRouges.length || nomDe(a).localeCompare(nomDe(b), "fr"),
  },
  nom: { label: "Nom de l'école", compare: (a, b) => nomDe(a).localeCompare(nomDe(b), "fr") },
  inscrits: {
    label: "Élèves inscrits",
    compare: (a, b) => b.inscrits - a.inscrits || nomDe(a).localeCompare(nomDe(b), "fr"),
  },
};

function nomDe(e: SuiviEcole): string {
  return e.nom ?? e.code ?? "";
}

function OuiNon({ oui, label }: { oui: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs ${
        oui ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
      }`}
    >
      {label} {oui ? "oui" : "non"}
    </span>
  );
}

function Pastilles({ ecole }: { ecole: SuiviEcole }) {
  if (ecole.feuxRouges.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
        <CircleCheck className="h-3 w-3" /> Aucune alerte
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <span
        className="inline-flex w-fit items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white"
        data-testid={`nb-alertes-${ecole.etablissementId}`}
      >
        <TriangleAlert className="h-3 w-3" /> {ecole.feuxRouges.length}
      </span>
      <ul className="flex flex-wrap gap-1">
        {ecole.feuxRouges.map((f) => (
          <li
            key={f.code}
            title={f.libelle}
            className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-red-600" aria-hidden />
            {f.libelle}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SedecSuivi() {
  const { data, isLoading, error } = useSuiviEcoles();
  const [tri, setTri] = useState<Tri>("alertes");

  const ecoles = useMemo(
    () => [...(data?.ecoles ?? [])].sort(TRIS[tri].compare),
    [data, tri],
  );
  const enAlerte = ecoles.filter((e) => e.feuxRouges.length > 0).length;
  const encaisse7j = ecoles.reduce((s, e) => s + e.encaisse7j, 0);
  const declares = ecoles.reduce((s, e) => s + e.declaresEnAttente, 0);

  const synthese = [
    { label: "Écoles suivies", valeur: String(ecoles.length), Icone: School, classe: "bg-slate-100 text-slate-700" },
    { label: "Écoles en alerte", valeur: String(enAlerte), Icone: TriangleAlert, classe: "bg-red-50 text-red-700" },
    { label: "Encaissé sur 7 jours", valeur: formatXof(encaisse7j), Icone: Wallet, classe: "bg-emerald-50 text-emerald-700" },
    { label: "Déclarations en attente", valeur: String(declares), Icone: Hourglass, classe: "bg-amber-50 text-amber-800" },
  ];

  return (
    <SedecShell>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Suivi des écoles</h1>
          <p className="text-muted-foreground mt-1">
            Une ligne par école active
            {data?.annee ? ` de l'année ${data.annee}` : " de l'année active"}. Les pastilles
            rouges signalent ce qui empêche une école de travailler ou les familles de payer : ce
            sont les écoles à appeler en premier.
          </p>
          {data?.calculeLe && (
            <p className="mt-1 text-xs text-muted-foreground">Calculé le {formatDateTime(data.calculeLe)}</p>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {synthese.map(({ label, valeur, Icone, classe }) => (
            <Card key={label} className="p-4">
              <div className="flex items-center gap-2">
                <span className={`rounded p-1.5 ${classe}`}><Icone className="h-4 w-4" /></span>
                <span className="text-2xl font-semibold">{valeur}</span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{label}</p>
            </Card>
          ))}
        </div>

        <div className="flex items-center gap-2 text-sm">
          <label htmlFor="tri-suivi" className="text-muted-foreground">Trier par</label>
          <select
            id="tri-suivi"
            value={tri}
            onChange={(e) => setTri(e.target.value as Tri)}
            className="rounded-md border border-input bg-background px-2 py-1"
          >
            {(Object.keys(TRIS) as Tri[]).map((t) => (
              <option key={t} value={t}>{TRIS[t].label}</option>
            ))}
          </select>
        </div>

        {isLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
            Le suivi des écoles n'a pas pu être chargé. {friendlyError(error)}
          </Card>
        )}

        {!isLoading && !error && (
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-left align-bottom">
                <tr>
                  <th className="p-3 font-medium">École</th>
                  <th className="p-3 font-medium">Alertes</th>
                  <th className="p-3 font-medium">Personnel actif</th>
                  <th className="p-3 font-medium">Élèves</th>
                  <th className="p-3 font-medium">Paramétrage</th>
                  <th className="p-3 font-medium">Encaissements</th>
                  <th className="p-3 font-medium">En attente</th>
                  <th className="p-3 font-medium">Activité sur 7 jours</th>
                </tr>
              </thead>
              <tbody>
                {ecoles.map((e) => (
                  <tr key={e.etablissementId} className="border-b align-top last:border-0">
                    <td className="p-3">
                      <div className="font-medium">{nomDe(e)}</div>
                      {e.code && <div className="font-mono text-xs text-muted-foreground">{e.code}</div>}
                    </td>
                    <td className="p-3 min-w-[14rem]"><Pastilles ecole={e} /></td>
                    <td className="p-3 whitespace-nowrap">
                      <div>Direction {e.direction} · Économe {e.econome} · Caisse {e.caisse}</div>
                      <div>Secrétariat et études {e.secretariatEtudes} · Enseignants {e.enseignants}</div>
                      <div className="text-xs text-muted-foreground">
                        Dernière connexion :{" "}
                        {e.derniereConnexion ? formatDateShort(e.derniereConnexion) : "jamais"}
                      </div>
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <div>{e.inscrits} inscrits</div>
                      <div>{e.sansClasse} sans classe</div>
                      <div>{e.parents} parents rattachés</div>
                    </td>
                    <td className="p-3">
                      <div className="whitespace-nowrap">{e.motifs} motifs · {e.echeances} tranches</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <span
                          className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs ${
                            e.reversementConfirme ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"
                          }`}
                        >
                          Reversement {e.reversementConfirme ? "confirmé" : "non confirmé"}
                        </span>
                        <OuiNon oui={e.orange} label="Orange" />
                        <OuiNon oui={e.jeko} label="Jèko" />
                      </div>
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <div>7 jours : {formatXof(e.encaisse7j)}</div>
                      <div>Année : {formatXof(e.encaisseAnnee)}</div>
                      <div className="text-xs text-muted-foreground">Dû de l'année : {formatXof(e.duAnnee)}</div>
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <div>
                        {e.declaresEnAttente} déclarés
                        {e.declaresEnAttentePlus48h > 0 && ` (dont ${e.declaresEnAttentePlus48h} depuis plus de 48 h)`}
                      </div>
                      <div>{e.enLigneBloques} en ligne bloqués</div>
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <div>{e.notes7j} notes · {e.appels7j} appels</div>
                      <div>{e.bulletinsPublies} bulletins publiés</div>
                    </td>
                  </tr>
                ))}
                {ecoles.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-muted-foreground">
                      Aucune école active sur l'année active de ce diocèse.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </SedecShell>
  );
}
